// WHY: libphonenumber-js/min (~20KB gz) covers RU/CN/US (founder targets);
// upgrade to /max only when a future caller needs a country /min lacks.
import { z } from 'zod';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';
import { ErrorCode } from '../errors/codes.js';

// WHY: gate on `isPossible()` (length + leading digit) rather than `isValid()`
// (full numbering-plan check), so syntactically correct E.164 placeholders such
// as the `+70000000000` test fixtures stay accepted. Combined with the
// `parsed.number === value` round-trip check this still rejects formatted
// input like `+7 (999) 123-45-67` and bare digits like `89991234567`.
function isStrictE164(value: string): boolean {
  let parsed;
  try {
    parsed = parsePhoneNumberFromString(value);
  } catch {
    return false;
  }
  return Boolean(parsed?.isPossible() && parsed.number === value);
}

/**
 * @aiPurpose strict E.164 phone validator. Rejects formatted input such as
 *   '+7 (999) 123-45-67' — only the canonical '+<country><digits>' form passes.
 * @aiInvariant accepted values round-trip byte-equal: `phoneSchema.parse(v) === v`.
 *   Failed parses carry `params.code = ErrorCode.INVALID_PHONE` on the issue.
 * @aiPii surfaces a phone — redact in logs.
 * @throws ValidationError with `params.code = ErrorCode.INVALID_PHONE`.
 */
export const phoneSchema = z.string().refine(isStrictE164, {
  message: 'invalid E.164 phone',
  params: { code: ErrorCode.INVALID_PHONE },
});

/**
 * @aiPurpose nullable variant of `phoneSchema` for columns that may be NULL.
 * @aiInvariant `null` passes; non-null values must satisfy `phoneSchema`.
 * @aiPii see `phoneSchema`.
 */
export const phoneSchemaNullable = phoneSchema.nullable();

/**
 * @aiPurpose normalize free-form phone input to E.164. Applies `defaultCountry`
 *   when the input lacks an explicit country prefix.
 * @aiInvariant returns `null` for unparseable input. Default country is `'RU'`;
 *   callers should pass an explicit country when known.
 * @aiPii returned value is a phone — redact in logs.
 */
export function parseToE164(input: string, defaultCountry: CountryCode = 'RU'): string | null {
  let parsed;
  try {
    parsed = parsePhoneNumberFromString(input, defaultCountry);
  } catch {
    return null;
  }
  return parsed?.isPossible() ? parsed.number : null;
}

/**
 * @aiPurpose format a strict E.164 phone for human display.
 * @aiInvariant `e164` must satisfy `phoneSchema`. `_displayCountry` is reserved
 *   for future country-aware formatting; libphonenumber-js's international
 *   format is presently country-independent.
 * @aiPii formatted output is a phone — redact in logs.
 * @throws Error with message 'formatPhone: invalid E.164 input' when the input
 *   is not a strict E.164 string.
 */
export function formatPhone(e164: string, _displayCountry?: CountryCode): string {
  let parsed;
  try {
    parsed = parsePhoneNumberFromString(e164);
  } catch {
    parsed = undefined;
  }
  if (!parsed?.isPossible() || parsed.number !== e164) {
    throw new Error('formatPhone: invalid E.164 input');
  }
  return parsed.formatInternational();
}
